import { supabaseConfiguration } from '../config/supabase';
import {
  SYNC_CANONICALIZATION_V2,
  SYNC_PROTOCOL_V1,
  SYNC_PROTOCOL_V2,
  type SyncBatch,
  type SyncBatchResult,
  type SyncProtocolVersion,
  type SyncPullResult,
  type SyncSnapshot
} from './types';

interface RemoteError {
  code?: string;
  message: string;
  status?: number;
}

interface RpcResult {
  data: unknown;
  error: RemoteError | null;
}

export interface SyncRpcClient {
  rpc(name: string, args?: Record<string, unknown>): PromiseLike<RpcResult>;
}

export interface SyncTransport {
  isRolloutAllowed(): Promise<boolean>;
  getSnapshot(protocolVersion?: SyncProtocolVersion): Promise<SyncSnapshot>;
  pull(afterChangeId: string, protocolVersion?: SyncProtocolVersion): Promise<SyncPullResult>;
  applyBatch(batch: SyncBatch): Promise<SyncBatchResult>;
}

export class SyncTransportError extends Error {
  constructor(
    message: string,
    readonly code: string,
    readonly retryable: boolean,
    readonly authenticationExpired = false
  ) {
    super(message);
    this.name = 'SyncTransportError';
  }
}

const remoteError = (error: RemoteError): SyncTransportError => {
  const normalized = `${error.code ?? ''} ${error.message}`.toLowerCase();
  const authenticationExpired = error.status === 401
    || normalized.includes('jwt')
    || normalized.includes('auth_required');
  const retryable = !authenticationExpired
    && error.status !== 403
    && !normalized.includes('rollout_denied')
    && !normalized.includes('invalid_');
  return new SyncTransportError(
    authenticationExpired ? 'A sessão expirou. Entre novamente para sincronizar.' : 'Não foi possível acessar a sincronização.',
    authenticationExpired ? 'authentication_expired' : error.code ?? 'remote_error',
    retryable,
    authenticationExpired
  );
};

const requireRecord = (value: unknown, label: string): Record<string, unknown> => {
  if (!value || typeof value !== 'object' || Array.isArray(value)) {
    throw new SyncTransportError(`Resposta inválida de ${label}.`, 'invalid_remote_response', false);
  }
  return value as Record<string, unknown>;
};

const rpc = async (client: SyncRpcClient, name: string, args?: Record<string, unknown>): Promise<unknown> => {
  const { data, error } = await client.rpc(name, args);
  if (error) throw remoteError(error);
  return data;
};

export const createSupabaseSyncTransport = (client: SyncRpcClient): SyncTransport => ({
  async isRolloutAllowed() {
    const value = await rpc(client, 'is_sync_enabled');
    if (typeof value !== 'boolean') throw new SyncTransportError('Resposta inválida de rollout.', 'invalid_remote_response', false);
    return value;
  },

  async getSnapshot(protocolVersion = SYNC_PROTOCOL_V1) {
    const protocolV2 = protocolVersion === SYNC_PROTOCOL_V2;
    const value = requireRecord(await rpc(
      client,
      protocolV2 ? 'get_sync_snapshot_protocol_v2' : 'get_sync_snapshot_v2'
    ), 'snapshot');
    if (typeof value.highWaterChangeId !== 'string' || !Array.isArray(value.entities)) {
      throw new SyncTransportError('Resposta inválida de snapshot.', 'invalid_remote_response', false);
    }
    if (protocolV2 && (
      value.protocolVersion !== SYNC_PROTOCOL_V2
      || value.canonicalization !== SYNC_CANONICALIZATION_V2
    )) throw new SyncTransportError('Envelope inválido de snapshot Protocol V2.', 'invalid_remote_response', false);
    return value as unknown as SyncSnapshot;
  },

  async pull(afterChangeId, protocolVersion = SYNC_PROTOCOL_V1) {
    const protocolV2 = protocolVersion === SYNC_PROTOCOL_V2;
    const value = requireRecord(await rpc(client, protocolV2
      ? 'pull_sync_changes_protocol_v2'
      : 'pull_sync_changes_v2', {
      after_change_id: afterChangeId,
      target_count: 500,
      max_batches: 50
    }), 'pull');
    if (!Array.isArray(value.changes) || typeof value.nextCursor !== 'string' || typeof value.hasMore !== 'boolean') {
      throw new SyncTransportError('Resposta inválida de pull.', 'invalid_remote_response', false);
    }
    if (protocolV2 && (
      value.protocolVersion !== SYNC_PROTOCOL_V2
      || value.canonicalization !== SYNC_CANONICALIZATION_V2
    )) throw new SyncTransportError('Envelope inválido de pull Protocol V2.', 'invalid_remote_response', false);
    return value as unknown as SyncPullResult;
  },

  async applyBatch(batch) {
    const rpcName = batch.protocolVersion === SYNC_PROTOCOL_V2
      ? 'apply_sync_batch_protocol_v2'
      : 'apply_sync_batch';
    const value = requireRecord(await rpc(client, rpcName, { batch }), rpcName);
    if (!['applied', 'conflict', 'rejected'].includes(String(value.status))) {
      throw new SyncTransportError(`Resposta inválida de ${rpcName}.`, 'invalid_remote_response', false);
    }
    return value as unknown as SyncBatchResult;
  }
});

export const syncTransport: SyncTransport | null = supabaseConfiguration.client
  ? createSupabaseSyncTransport(supabaseConfiguration.client as unknown as SyncRpcClient)
  : null;
